
import { createBuffer, createVertexArray, describeAttributes } from "./gl/buffer";
import { createProgram, uniformLocations, type Uniforms } from "./gl/shader";
import { decodeTga, type RgbaImage } from "./image";
import { MM_PER_LEGACY_UNIT, type MapEnvironment } from "./mapassets";
import type { Mat4 } from "./view";

/** Where `tools/build_environment.mjs` puts the records and the textures they
 *  name. The same directory `ENVIRONMENT_INDEX_URL` is in. */
const ENVIRONMENT_DIR = "environment/";

export function buildSkyBox(environment: MapEnvironment): {
  readonly positions: Float32Array;
  readonly colours: Float32Array;
  readonly indices: Uint16Array;
} | null {
  const upper = Math.max(0, Math.trunc(environment.skyBox.gradientLevelUpper));
  const lower = Math.max(0, Math.trunc(environment.skyBox.gradientLevelLower));
  if (upper + lower <= 0) return null;

  const rows = environment.skyBox.gradient;
  const bands = rows.length === (upper + lower) * 2 ? upper + lower : 0;
  const first = (band: number): readonly [number, number, number] =>
    band < bands ? [rows[band * 2][0], rows[band * 2][1], rows[band * 2][2]] : [0, 0, 0];
  const second = (band: number): readonly [number, number, number] =>
    band < bands
      ? [rows[band * 2 + 1][0], rows[band * 2 + 1][1], rows[band * 2 + 1][2]]
      : [0, 0, 0];

  const positions: number[] = [];
  const colours: number[] = [];
  const indices: number[] = [];

  /** One quad, in the original's own logical vertex order (0, 1, 2, 3).
   *  `CSkyObjectQuad` stores them permuted by `m_Indices = {0, 2, 1, 3}` and
   *  draws the buffer as a four-vertex triangle strip (`SkyBox.cpp:16-19`,
   *  `:91-95`), so the two triangles are (0, 2, 1) and (2, 1, 3). */
  const quad = (
    corners: ReadonlyArray<readonly [number, number, number]>,
    tints: ReadonlyArray<readonly [number, number, number]>,
  ): void => {
    const base = positions.length / 3;
    for (let at = 0; at < 4; at += 1) {
      positions.push(corners[at][0], corners[at][1], corners[at][2]);
      colours.push(tints[at][0], tints[at][1], tints[at][2]);
    }
    indices.push(base + 0, base + 2, base + 1, base + 2, base + 1, base + 3);
  };

  // The four vertical faces, each a pair of (x, y) edges walked from the zenith
  // down. `CSkyBox::Refresh`'s own order and its own corners (`:442-464`).
  const sides: ReadonlyArray<readonly [readonly [number, number], readonly [number, number]]> = [
    [[1, -1], [-1, -1]], // front
    [[-1, 1], [1, 1]], // back
    [[-1, -1], [-1, 1]], // left
    [[1, 1], [1, -1]], // right
  ];
  for (const [edge0, edge1] of sides) {
    let band = 0;
    for (let step = 0; step < upper; step += 1, band += 1) {
      // `SetSkyObjectQuadVertical`, `:331-359`: vertices 0 and 2 sit at the
      // band's lower edge and take the second colour, 1 and 3 at its upper edge
      // and take the first.
      const zLow = 1 - (step + 1) / upper;
      const zHigh = 1 - step / upper;
      quad(
        [
          [edge0[0], edge0[1], zLow],
          [edge0[0], edge0[1], zHigh],
          [edge1[0], edge1[1], zLow],
          [edge1[0], edge1[1], zHigh],
        ],
        [second(band), first(band), second(band), first(band)],
      );
    }
    for (let step = 0; step < lower; step += 1, band += 1) {
      // `:360-388`: the same shape below the horizon, z running 0 to -1.
      const zLow = -(step + 1) / lower;
      const zHigh = -step / lower;
      quad(
        [
          [edge0[0], edge0[1], zLow],
          [edge0[0], edge0[1], zHigh],
          [edge1[0], edge1[1], zLow],
          [edge1[0], edge1[1], zHigh],
        ],
        [second(band), first(band), second(band), first(band)],
      );
    }
  }

  // Top: flat in band 0's first colour, the zenith (`:467-472`, `:677-727`).
  const zenith = first(0);
  quad(
    [[1, 1, 1], [-1, 1, 1], [1, -1, 1], [-1, -1, 1]],
    [zenith, zenith, zenith, zenith],
  );
  // Bottom: flat in the last band's second colour, the nadir (`:475-480`,
  // `:728-778`).
  const nadir = second(upper + lower - 1);
  quad(
    [[-1, 1, -1], [1, 1, -1], [-1, -1, -1], [1, -1, -1]],
    [nadir, nadir, nadir, nadir],
  );

  return {
    positions: new Float32Array(positions),
    colours: new Float32Array(colours),
    indices: new Uint16Array(indices),
  };
}

export function cloudOffset(
  speed: readonly [number, number],
  seconds: number,
): readonly [number, number] {
  const wrap = (value: number): number => {
    if (!Number.isFinite(value)) return 0;
    const folded = value % 1;
    return folded < 0 ? folded + 1 : folded;
  };
  return [wrap(speed[0] * seconds), wrap(speed[1] * seconds)];
}

export function cloudTextureUrl(environment: MapEnvironment): string | null {
  const named = environment.skyBox.cloudTextureFileName;
  if (named === null || named.trim() === "") return null;
  const base = named.replace(/\\/g, "/").split("/").pop();
  if (base === undefined || base === "") return null;
  return `${ENVIRONMENT_DIR}${base.toLowerCase()}`;
}

/** Fetch and decode a record's cloud texture. Null -- never a throw -- for a
 *  record with no texture, a 404, or bytes that are not a 32-bit Targa: the
 *  original draws no cloud in exactly those cases too (`SkyBox.cpp:871-873`). */
export async function loadCloudTexture(environment: MapEnvironment): Promise<RgbaImage | null> {
  const url = cloudTextureUrl(environment);
  if (url === null) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return decodeTga(await response.arrayBuffer());
  } catch {
    return null;
  }
}

const SKY_VERTEX_SOURCE = `#version 300 es
precision highp float;

layout(location = 0) in vec3 a_corner;
layout(location = 1) in vec3 a_colour;

uniform mat4 u_viewProj;
uniform vec3 u_eyeMm;
uniform vec3 u_scaleMm;

out vec3 v_colour;

void main() {
  v_colour = a_colour;
  gl_Position = u_viewProj * vec4(a_corner * u_scaleMm + u_eyeMm, 1.0);
}
`;

const SKY_FRAGMENT_SOURCE = `#version 300 es
precision highp float;

in vec3 v_colour;
out vec4 fragColour;

void main() {
  // D3DTOP_SELECTARG2 with ARG2 = D3DTA_DIFFUSE (SkyBox.cpp:813-815): the
  // gradient mode ignores the texture stage entirely and writes the
  // interpolated vertex colour.
  fragColour = vec4(v_colour, 1.0);
}
`;

const CLOUD_VERTEX_SOURCE = `#version 300 es
precision highp float;

layout(location = 0) in vec3 a_corner;
layout(location = 1) in vec2 a_uv;

uniform mat4 u_viewProj;
uniform vec3 u_originMm;
uniform vec2 u_scaleMm;
uniform vec2 u_textureScale;
uniform vec2 u_textureOffset;

out vec2 v_uv;

void main() {
  // The D3DTTFF_COUNT2 texture matrix: u' = u * _11 + _31 (SkyBox.cpp:298-299,
  // :886-887).
  v_uv = a_uv * u_textureScale + u_textureOffset;
  gl_Position = u_viewProj * vec4(
    a_corner.xy * u_scaleMm + u_originMm.xy, u_originMm.z, 1.0);
}
`;

const CLOUD_FRAGMENT_SOURCE = `#version 300 es
precision highp float;

in vec2 v_uv;

uniform sampler2D u_cloud;
uniform vec3 u_diffuse;

out vec4 fragColour;

void main() {
  vec4 texel = texture(u_cloud, v_uv);
  // D3DTOP_MODULATEINVALPHA_ADDCOLOR, ARG1 = TEXTURE, ARG2 = DIFFUSE
  // (SkyBox.cpp:903-905): arg1.rgb + arg2.rgb * (1 - arg1.a).
  fragColour = vec4(texel.rgb + u_diffuse * (1.0 - texel.a), 1.0);
}
`;

/** The cloud quad: `(±1, ±1, 0)` with the horizon face's own texture corners
 *  (`SkyBox.cpp:410-428`, `:553-557`), expanded from the four-vertex strip
 *  (0, 2, 1, 3) into two triangles. x, y, z, u, v. */
const CLOUD_QUAD = new Float32Array([
  // triangle (0, 2, 1)
  1, 1, 0, 0, 1,
  1, -1, 0, 1, 1,
  -1, 1, 0, 0, 0,
  // triangle (2, 1, 3)
  1, -1, 0, 1, 1,
  -1, 1, 0, 0, 0,
  -1, -1, 0, 1, 0,
]);

const SKY_UNIFORMS = ["u_viewProj", "u_eyeMm", "u_scaleMm"] as const;
const CLOUD_UNIFORMS = [
  "u_viewProj",
  "u_originMm",
  "u_scaleMm",
  "u_textureScale",
  "u_textureOffset",
  "u_cloud",
  "u_diffuse",
] as const;

/** The camera's own eye, in millimetres -- `CCamera::GetEye`, which is what the
 *  cube and the cloud sheet are both parked on (`SkyBox.cpp:131-145`). */
export interface SkyView {
  readonly viewProj: Mat4;
  readonly eyeMm: { readonly xMm: number; readonly yMm: number; readonly zMm: number };
}

export class SkyBox {
  private readonly skyProgram: WebGLProgram;
  private readonly skyUniforms: Uniforms<(typeof SKY_UNIFORMS)[number]>;
  private readonly skyVao: WebGLVertexArrayObject;
  private readonly skyPositions: WebGLBuffer;
  private readonly skyColours: WebGLBuffer;
  private readonly skyIndices: WebGLBuffer;
  private skyIndexCount = 0;

  private readonly cloudProgram: WebGLProgram;
  private readonly cloudUniforms: Uniforms<(typeof CLOUD_UNIFORMS)[number]>;
  private readonly cloudVao: WebGLVertexArrayObject;
  private cloudTexture: WebGLTexture | null = null;

  private environment: MapEnvironment | null = null;

  constructor(private readonly gl: WebGL2RenderingContext) {
    this.skyProgram = createProgram(gl, SKY_VERTEX_SOURCE, SKY_FRAGMENT_SOURCE, "skybox");
    this.skyUniforms = uniformLocations(gl, this.skyProgram, SKY_UNIFORMS);
    this.skyVao = createVertexArray(gl, "skybox");
    this.skyPositions = createBuffer(gl, "skybox positions");
    this.skyColours = createBuffer(gl, "skybox colours");
    this.skyIndices = createBuffer(gl, "skybox indices");
    gl.bindVertexArray(this.skyVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.skyPositions);
    describeAttributes(gl, this.skyProgram, 12, [
      { name: "a_corner", components: 3, type: gl.FLOAT, normalized: false, offset: 0, divisor: 0 },
    ]);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.skyColours);
    describeAttributes(gl, this.skyProgram, 12, [
      { name: "a_colour", components: 3, type: gl.FLOAT, normalized: false, offset: 0, divisor: 0 },
    ]);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.skyIndices);

    this.cloudProgram = createProgram(gl, CLOUD_VERTEX_SOURCE, CLOUD_FRAGMENT_SOURCE, "cloud");
    this.cloudUniforms = uniformLocations(gl, this.cloudProgram, CLOUD_UNIFORMS);
    this.cloudVao = createVertexArray(gl, "cloud");
    gl.bindVertexArray(this.cloudVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, createBuffer(gl, "cloud quad"));
    // `SetSkyObjectQuadHorizon` with the cloud's own corners
    // (`SkyBox.cpp:553-557`, `:403-429`), written out as the two triangles the
    // strip (0, 2, 1, 3) makes: x, y, z, u, v.
    gl.bufferData(gl.ARRAY_BUFFER, CLOUD_QUAD, gl.STATIC_DRAW);
    describeAttributes(gl, this.cloudProgram, 20, [
      { name: "a_corner", components: 3, type: gl.FLOAT, normalized: false, offset: 0, divisor: 0 },
      { name: "a_uv", components: 2, type: gl.FLOAT, normalized: false, offset: 12, divisor: 0 },
    ]);
    gl.bindVertexArray(null);
  }

  setEnvironment(environment: MapEnvironment, cloud: RgbaImage | null): void {
    const gl = this.gl;
    this.environment = environment;
    const built = buildSkyBox(environment);
    gl.bindVertexArray(this.skyVao);
    if (built === null) {
      this.skyIndexCount = 0;
    } else {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.skyPositions);
      gl.bufferData(gl.ARRAY_BUFFER, built.positions, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.skyColours);
      gl.bufferData(gl.ARRAY_BUFFER, built.colours, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.skyIndices);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, built.indices, gl.STATIC_DRAW);
      this.skyIndexCount = built.indices.length;
    }
    gl.bindVertexArray(null);

    if (this.cloudTexture !== null) {
      gl.deleteTexture(this.cloudTexture);
      this.cloudTexture = null;
    }
    if (cloud !== null) {
      const texture = gl.createTexture();
      if (texture !== null) {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(
          gl.TEXTURE_2D, 0, gl.RGBA, cloud.width, cloud.height, 0,
          gl.RGBA, gl.UNSIGNED_BYTE, cloud.pixels,
        );
        // Repeat, because the sheet is scrolled past 1 and the original never
        // clamps it -- `CSkyBox::Render` sets CLAMP only for the textured-face
        // mode and restores it before the cloud pass (`SkyBox.cpp:831-848`).
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.bindTexture(gl.TEXTURE_2D, null);
        this.cloudTexture = texture;
      }
    }
  }

  draw(view: SkyView, seconds: number): void {
    const environment = this.environment;
    if (environment === null) return;
    const gl = this.gl;
    const eye = view.eyeMm;

    gl.disable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.disable(gl.BLEND);

    if (this.skyIndexCount > 0) {
      const scale = environment.skyBox.scale;
      gl.useProgram(this.skyProgram);
      gl.bindVertexArray(this.skyVao);
      gl.uniformMatrix4fv(this.skyUniforms.u_viewProj, false, view.viewProj);
      gl.uniform3f(this.skyUniforms.u_eyeMm, eye.xMm, eye.yMm, eye.zMm);
      gl.uniform3f(
        this.skyUniforms.u_scaleMm,
        scale[0] * MM_PER_LEGACY_UNIT,
        scale[1] * MM_PER_LEGACY_UNIT,
        scale[2] * MM_PER_LEGACY_UNIT,
      );
      gl.drawElements(gl.TRIANGLES, this.skyIndexCount, gl.UNSIGNED_SHORT, 0);
    }

    if (this.cloudTexture !== null) {
      const sky = environment.skyBox;
      const offset = cloudOffset(sky.cloudSpeed, seconds);
      gl.useProgram(this.cloudProgram);
      gl.bindVertexArray(this.cloudVao);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.cloudTexture);
      gl.uniform1i(this.cloudUniforms.u_cloud, 0);
      gl.uniformMatrix4fv(this.cloudUniforms.u_viewProj, false, view.viewProj);
      gl.uniform3f(
        this.cloudUniforms.u_originMm,
        eye.xMm,
        eye.yMm,
        eye.zMm + sky.cloudHeight * MM_PER_LEGACY_UNIT,
      );
      gl.uniform2f(
        this.cloudUniforms.u_scaleMm,
        sky.cloudScale[0] * MM_PER_LEGACY_UNIT,
        sky.cloudScale[1] * MM_PER_LEGACY_UNIT,
      );
      gl.uniform2f(
        this.cloudUniforms.u_textureScale,
        sky.cloudTextureScale[0],
        sky.cloudTextureScale[1],
      );
      gl.uniform2f(this.cloudUniforms.u_textureOffset, offset[0], offset[1]);
      const tint = sky.cloudColour[0] ?? [0, 0, 0, 0];
      gl.uniform3f(this.cloudUniforms.u_diffuse, tint[0], tint[1], tint[2]);
      // SRCBLEND ONE, DESTBLEND INVSRCCOLOR (`SkyBox.cpp:881-882`).
      gl.enable(gl.BLEND);
      gl.blendEquation(gl.FUNC_ADD);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_COLOR);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.disable(gl.BLEND);
      gl.bindTexture(gl.TEXTURE_2D, null);
    }

    gl.bindVertexArray(null);
    // Handed back the way the terrain pass expects to find it: it turns the
    // test on itself, but nothing else in the frame turns the *write* back on.
    gl.depthMask(true);
  }
}

